import { useCallback, useEffect, useRef, useState } from 'react';
import { InputBase, IconButton, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from 'src/assets/images/icons/search-normal.svg';
import debounce from 'lodash/debounce';
import { useGeneralSearch } from 'src/use-cases/dashboard/useGeneralSearch';
import { ResultRecord } from 'src/generated/services/TFinancialApi';
import { SearchBarResults } from './Results';

const SearchBarRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isFocused',
})<{
  isFocused?: boolean;
}>(({ isFocused, theme }) => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '4px 4px 4px 8px',
  height: '40px',
  background: '#30383C',
  border: isFocused ? '1px solid #9F9F9F' : '1px solid #495053',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: '400',
  position: 'relative',
  [theme.breakpoints.down(1520)]: {
    height: '35px',
  },
}));

const SearchInput = styled(InputBase)(({ theme }) => ({
  flexGrow: 1,
  fontSize: '14px',
  fontWeight: '400',
  color: '#eeeeee',
  '&.focused input::placeholder': {
    color: '#9F9F9F',
    opacity: 1,
  },
  '&.not-focused input::placeholder': {
    color: '#9F9F9F',
    opacity: 1,
  },
  [theme.breakpoints.down(1520)]: {
    fontSize: '12px',
  },
}));

const SearchIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: '#00A651',
  borderRadius: '4px',
  width: '32px',
  height: '32px',
  padding: '0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    backgroundColor: '#008f45',
  },
  [theme.breakpoints.down(1520)]: {
    width: '28px',
    height: '28px',
  },
}));

const SearchIconImage = styled('img')(({ theme }) => ({
  width: '20px',
  height: '20px',
  [theme.breakpoints.down(1520)]: {
    width: '16px',
    height: '16px',
  },
}));

interface SearchBarProps {
  placeholder: string;
  onSubmit: (keyword: string) => void;
  currentKeyword: string;
  inputClassName?: string;
}

export const SearchBar: React.FC<SearchBarProps> = ({ placeholder, onSubmit, currentKeyword, inputClassName }) => {
  const [keyword, setKeyword] = useState(currentKeyword ?? '');
  const [isFocused, setIsFocused] = useState(false);
  const [searchResult, setSearchResult] = useState<ResultRecord[]>([]);
  const [isShowContent, setIsShowContent] = useState(false);
  const [totalRecord, setTotalRecord] = useState(0);
  const [page, setPage] = useState(1);
  const [onlyShowApproved, setOnlyShowApproved] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const { mutateAsync: generalSearch, isLoading: isLoadingSearch } = useGeneralSearch();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onInputChange = useCallback(
    debounce(async (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target?.value?.toUpperCase();
      if (value && value?.length >= 3) {
        const result = await generalSearch({
          searchKey: value,
          pageNumber: page,
          pageSize: 10,
          isApprovedInvoice: onlyShowApproved,
        });

        setKeyword(value);
        setSearchResult(result?.data?.data?.results ?? []);
        setTotalRecord(result?.data?.data?.totalRecord ?? 0);
        setIsShowContent(true);
      } else {
        setSearchResult([]);
        setTotalRecord(0);
        setIsShowContent(false);
        setPage(1);
      }
    }, 1000),
    []
  );

  const onChangeOnlyShowApproved = useCallback(
    async (status: boolean) => {
      setOnlyShowApproved(status);

      const result = await generalSearch({
        searchKey: keyword,
        pageNumber: 0,
        pageSize: 10,
        isApprovedInvoice: status,
      });

      setSearchResult(result?.data?.data?.results ?? []); // Update results based on the toggle
      setTotalRecord(result?.data?.data?.totalRecord ?? 0); // Update the total record count
    },
    [generalSearch, keyword]
  );

  const onLoadMore = useCallback(async () => {
    // Prevent API call if all records are already loaded
    if (searchResult.length >= totalRecord) {
      return;
    }
    const newPage = page + 1;
    setPage(newPage);
    const result = await generalSearch({
      searchKey: keyword,
      pageNumber: newPage,
      pageSize: 10,
      isApprovedInvoice: onlyShowApproved,
    });

    setSearchResult((current) => [...current, ...(result?.data?.data?.results ?? [])]);
  }, [page, keyword, generalSearch, onlyShowApproved, searchResult, totalRecord]);

  const onClearSearch = useCallback(async () => {
    setKeyword('');
    setSearchResult([]);
    setTotalRecord(0);
    setIsShowContent(false);
    setPage(1);
  }, []);

  /**
   * Alert if clicked on outside of element
   */
  function handleClickOutside(event) {
    const ddMenu = document.getElementById('menu-');
    const triggerMenu = document.getElementById('btn-trigger-notif');
    const inDdMenu = ddMenu && ddMenu.contains(event.target);
    const inTriggerMenu = triggerMenu && triggerMenu.contains(event.target);
    if (!contentRef?.current?.contains(event.target) && !inDdMenu && !inTriggerMenu) {
      setIsShowContent(false);
      setIsFocused(false);
    }
  }
  useEffect(() => {
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [contentRef, setIsShowContent]);

  return (
    <div className='relative'>
      <SearchBarRoot isFocused={isFocused}>
        <SearchInput
          className={`${isFocused ? 'focused' : 'not-focused'} ${inputClassName || ''}`}
          placeholder={placeholder}
          value={keyword}
          onChange={(event) => {
            setKeyword(event.target.value);
          }}
          onInput={onInputChange}
          onFocus={() => {
            setIsFocused(true);
            if (searchResult?.length > 0) {
              setIsShowContent(true);
            }
          }}
          onBlur={() => setIsFocused(false)}
        />

        {keyword && keyword !== '' && (
          <div className='absolute top:-[1px] xl:top-[3px] right-[38px]'>
            <IconButton disabled={isLoadingSearch} onClick={onClearSearch}>
              <CloseIcon sx={{ fontSize: 18, color: '#9F9F9F' }} />
            </IconButton>
          </div>
        )}

        <SearchIconButton className='pointer-events-none' disabled={isLoadingSearch} type='button' onClick={() => onSubmit(keyword)}>
          <SearchIconImage src={SearchIcon} alt='Search' />
        </SearchIconButton>
      </SearchBarRoot>
      <SearchBarResults
        contentRef={contentRef}
        results={searchResult}
        totalRecord={totalRecord}
        isLoading={isLoadingSearch}
        isShowContent={isShowContent}
        onLoadMore={onLoadMore}
        hasMore={searchResult.length < totalRecord}
        isOnlyShowApproved={onlyShowApproved}
        onChangeOnlyShowApproved={onChangeOnlyShowApproved}
        keyword={keyword}
      />
    </div>
  );
};
